import React, { SetStateAction, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import {
	FrontendCompanyWithAdditionalInfomation,
	FrontendUserInfo,
} from "node-library/dist/portal";
import {
	SampleWithInstanceAndSpecSortColumn,
	TestBatchSortColumn,
	OrderTableSortColumn,
	SortOrder,
	UnionTestBatchSortColumn,
} from "node-library/dist/paginations";

import {
	AdminViewOrderWrapperPage,
	FrontendContentPage,
	FrontendInstanceTestStatusFilter,
	FrontendTestBatchStatusFilter,
	OrderStatus,
	Severity,
	TestStatus,
} from "node-library/dist/constants";

import { Pagination } from "@mui/material";
import { useSnackbar } from "../../../common-components/toast/showToastMessage";
import { sendGETRequest } from "../../../requestHelper/sendRequest";
import { RedErrorIcon } from "../../../common-components/toast/errorIcons";
import {
	getSampleWithInstanceAndSpecSortString,
	getInstanceStatusFilterTranslationText,
	getTestStatusTranslationText,
} from "../../../helpers/sampleInstanceHelper";
import {
	CustomDropdown,
	CustomRadioGroup,
	CustomSearchBar,
	RowDropdown,
} from "../../../common-components/inputs";
import { SortArrowIcon } from "../../../common-components/svgIcons";
import { NeutralColor, SecondaryColor } from "node-library/dist/colors";
import InstancePageSkeletonUI from "./batchPageSkeleton";

import AdminViewBatchContent from "./adminViewBatchContent";
import BatchPageSkeletonUI from "./batchPageSkeleton";
import { BlackButton } from "../../../common-components/button";

import { FrontendSampleTestBatchOverview } from "node-library/dist/structures/frontendSampleTestBatchPageDefinitions";
import {
	getBatchStatusFilterTranslationText,
	getTestBatchSortString,
	getUnionTestBatchSortString,
} from "../../../helpers/testBatchHelper";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;

	user: FrontendUserInfo | undefined;

	navigateToOrderDetailsPage: (
		orderId: string,
		fromPage: FrontendContentPage
	) => void;

	navigateToBatchDetailsPage: (
		batchId: string,
		fromPage: FrontendContentPage
	) => void;

	setUnsavedChanges: React.Dispatch<SetStateAction<boolean>>;
}

export default function AdminViewBatchPage(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const { setUnsavedChanges, company, user } = props;

	const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);

	const [batchOverviews, setBatchOverviews] = useState<
		Array<FrontendSampleTestBatchOverview>
	>([]);

	const [loading, setLoading] = useState<boolean>(false);
	const [searchText, setSearchText] = useState<string>("");

	// const [selectedCategoryId, setSelectedCategoryId] = useState<string>(
	// 	company.companyTestCategories && company.companyTestCategories.length > 0
	// 		? company.companyTestCategories[0].testCategoryId
	// 		: ""
	// );

	//paginations
	const [totalPageCount, setTotalPageCount] = useState<number>(0);
	const [sortBy, setSortBy] = useState<UnionTestBatchSortColumn>(
		UnionTestBatchSortColumn.DUE_DATE
	);
	const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.ASC);
	const [currentPagination, setCurrentPagination] = useState<number>(1);

	const [batchStatusFilterOption, setBatchStatusFilterOption] = useState<
		FrontendTestBatchStatusFilter | ""
	>(FrontendTestBatchStatusFilter.IN_PROGRESS);

	function toggleSortOrder() {
		if (sortOrder === SortOrder.ASC) {
			setSortOrder(SortOrder.DESC);
		} else {
			setSortOrder(SortOrder.ASC);
		}
	}

	async function getBatchOverviews(searchText: string) {
		setLoading(true);
		try {
			const response = await sendGETRequest(
				"/sample-test-batches/get-batch-overviews",
				{
					searchText,
					sortBy,
					sortOrder,
					pageNumber: currentPagination,
					batchStatusFilterOption,
					// selectedCategoryId,
				}
			);

			const frontendBatchOverviews: Array<FrontendSampleTestBatchOverview> =
				response?.data?.frontendBatchOverviews;
			const pageCount: number = response?.data?.totalPages;
			setBatchOverviews(frontendBatchOverviews);
			setTotalPageCount(pageCount);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		} finally {
			setLoading(false);
		}
	}

	// const categoryDropdownOptions: Array<{
	// 	title: string;
	// 	value: string;
	// 	renderValue?: string;
	// }> = company.companyTestCategories.map((category) => ({
	// 	title: category.testCategoryName,
	// 	value: category.testCategoryId,
	// }));

	// const selectCategoryDropdown: React.ReactNode = (
	// 	<CustomDropdown
	// 		id="category-dropdown"
	// 		label={translation("TestCategory")}
	// 		options={categoryDropdownOptions}
	// 		value={selectedCategoryId}
	// 		setValue={setSelectedCategoryId}
	// 		displayEmpty
	// 	/>
	// );

	const statusSelectionOptions: Array<{
		key: string;
		name: string;
		description?: string;
		value: any;
		children?: any;
	}> = Object.values(FrontendTestBatchStatusFilter).map(
		(statusFilter: FrontendTestBatchStatusFilter) => {
			const name: string = getBatchStatusFilterTranslationText(
				statusFilter,
				translation
			);
			return {
				key: statusFilter,
				name: name,
				description: "",
				value: statusFilter,
				children: (
					<div
						className={`center-text status-selection-radio-option typography-body-02 ${
							batchStatusFilterOption === statusFilter
								? "status-selection-radio-option-selected"
								: ""
						}`}
					>
						{name}
					</div>
				),
			};
		}
	);

	const statusSelection: React.ReactNode = (
		<div
			style={{
				maxWidth: "700px",
				overflowX: "auto",
			}}
		>
			<CustomRadioGroup
				options={statusSelectionOptions}
				selectedOption={batchStatusFilterOption}
				onChange={(e) => {
					setBatchStatusFilterOption(e.target.value);
				}}
				className="status-selection-radio-group"
			/>
		</div>
	);

	const sortOptionSelection: React.ReactNode = (
		<div
			style={{
				display: "flex",
				alignItems: "center",
				gap: "5px",
				marginLeft: "auto",
			}}
		>
			<div
				onClick={() => {
					toggleSortOrder();
				}}
				style={{ cursor: "pointer" }}
			>
				<SortArrowIcon sortOrder={sortOrder} />
			</div>
			<RowDropdown
				id="order-sort-order-dropdown"
				className="sort-order-dropdown"
				label={"a"}
				labelClassName="hide"
				options={Object.values(UnionTestBatchSortColumn).map((value) => {
					return {
						value: value,
						title: getUnionTestBatchSortString(value, translation),
					};
				})}
				value={sortBy}
				setValue={setSortBy}
				noBorder
			></RowDropdown>
		</div>
	);

	useEffect(() => {
		getBatchOverviews(searchText);
	}, [
		// selectedCategoryId,
		currentPagination,
		sortBy,
		sortOrder,
		batchStatusFilterOption,
	]);

	const viewOrderTitle: React.ReactNode = (
		<div className="view-orders-page-header">
			<div className="typography-heading-01">
				{translation("SampleBatches")}
			</div>
			<CustomSearchBar
				id="view-order-page-searchbar"
				searchText={searchText}
				setSearchText={setSearchText}
				placeholder={translation("SearchByOrderNumberOrClientCompanyName")}
				searchButtonText={translation("Search")}
				onSearch={() => {
					if (currentPagination !== 1) {
						setCurrentPageIndex(1);
					} else {
						getBatchOverviews(searchText);
					}
				}}
			></CustomSearchBar>
		</div>
	);

	let currentComponent: JSX.Element = <></>;
	switch (currentPageIndex) {
		case 0:
			currentComponent = (
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						gap: "20px",
						marginTop: "60px",
						minHeight: "90vh",
					}}
				>
					<div
						style={{
							flex: "1",
							display: "flex",
							flexDirection: "column",
							gap: "20px",
						}}
					>
						{viewOrderTitle}
						{/* {selectCategoryDropdown} */}
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								flexWrap: "wrap",
								gap: "10px",
							}}
						>
							{statusSelection}
							{sortOptionSelection}
						</div>
						{loading ? (
							<BatchPageSkeletonUI />
						) : (
							<AdminViewBatchContent
								company={props.company}
								batchOverviews={batchOverviews}
								user={props.user}
								// sortBy={sortBy}
								// setSortBy={setSortBy}
								// sortOrder={sortOrder}
								// setSortOrder={setSortOrder}
								navigateToOrderDetailsPage={props.navigateToOrderDetailsPage}
								navigateToBatchDetailsPage={props.navigateToBatchDetailsPage}
							/>
						)}
					</div>
					{!loading && batchOverviews.length > 0 && (
						<div
							style={{
								display: "flex",
								justifyContent: "center",
							}}
						>
							<Pagination
								count={totalPageCount}
								page={currentPagination}
								onChange={(e, value) => {
									setCurrentPagination(value);
								}}
								sx={{
									backgroundColor: "white",
									borderRadius: "30px",
									padding: "6px 16px",

									"& .MuiPaginationItem-root": {
										backgroundColor: "white",
										color: "black",
										border: "1px solid transparent", // Hide outline when not selected
										"&:hover": {
											backgroundColor: "#f5f5f5", // Change background on hover
										},
										borderRadius: "50%",
									},
									"& .Mui-selected": {
										backgroundColor: SecondaryColor.SECONDARY_50, // Change background for selected item
										border: `1px solid ${NeutralColor.NEUTRAL_GREY_950}`, // Change text color for selected item
									},
								}}
								variant="outlined"
								shape="rounded"
							/>
						</div>
					)}
				</div>
			);
			break;
	}

	return <div style={{ margin: "0 auto" }}>{currentComponent}</div>;
}
